import { useState } from 'react';
import { ErroreApi } from '../api/cliente.js';
import { useAccessoOperatore, useSalvaAccesso } from '../api/credenziali.js';
import { etichettaLivello } from '../etichette.js';
import { data } from '../formati.js';
import { t } from '../i18n/lingua.js';

/**
 * L'accesso di un operatore, dentro la sua scheda.
 *
 * Nel v2 sta esattamente qui — sotto i dati anagrafici, quando il tipo è
 * «operatore» — e nella 3.0 mancava del tutto: `identity_credentials` si
 * leggeva soltanto, quindi **creare un operatore non gli dava l'accesso**.
 * Nei dati sono 17 gli operatori attivi.
 *
 * Tre cose deliberate:
 *
 * **La password non si legge, si riscrive.** A database c'è un'impronta, e
 * l'impronta non torna indietro. Il campo è vuoto e vuoto resta: scriverci
 * dentro vuol dire dare una password nuova.
 *
 * **Sospendere non è cancellare.** L'utenza resta, con tutto quello che ha
 * fatto attaccato; semplicemente non entra più. Cancellare un operatore che
 * ha firmato ottomila righe di storico non si può.
 *
 * **Chi cambia la password a qualcuno gli chiude anche le sessioni.** Se
 * gliel'hanno rubata, lasciare aperto chi è dentro non serve a niente.
 *
 * Qui si scelgono anche **il livello e le aziende**, come nel v2. Nella 3.0
 * non li scriveva nessuno: un operatore nuovo nasceva senza livello e con
 * la sola azienda in cui l'aveva creato chi lo creava.
 */

const LIVELLI = [
  'administrator',
  'manager',
  'exportmanager',
  'salesman',
  'agent',
  'accountant',
  'quality',
  'hrmanager',
  // vede solo «Da confermare», senza prezzi (Rita, 26 ottobre 2026)
  'production',
] as const;

/**
 * «Nessun accesso» non c'è: era un limite di Instant Developer Cloud. Chi non
 * deve entrare non ha la password, o è sospeso; un operatore nuovo nasce
 * senza livello, e per dargli l'accesso se ne sceglie uno.
 */
export function SchedaAccesso({ id, nome }: { id: number; nome: string }) {
  const [email, setEmail] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [conferma, setConferma] = useState('');
  const [livello, setLivello] = useState<string | null>(null);
  const [aziende, setAziende] = useState<number[] | null>(null);

  const accesso = useAccessoOperatore(id, true);
  const salva = useSalvaAccesso(id);

  /**
   * Il livello di accesso lo controlla il servizio: se chi guarda non è
   * amministratore la richiesta torna 403, e la scheda non si mostra invece
   * di mostrare campi che non salveranno mai.
   */
  if (accesso.error) return null;
  if (accesso.isPending) return null;

  const a = accesso.data!;
  const valoreEmail = email ?? a.email ?? '';
  const diverse = conferma.length > 0 && password !== conferma;
  const valoreLivello = livello ?? a.accessLevel;
  const valoreAziende = aziende ?? a.companyIds;
  const stesseAziende =
    [...valoreAziende].sort().join(',') === [...a.companyIds].sort().join(',');
  const credenzialiCambiate = valoreEmail !== (a.email ?? '') || password.length > 0;
  const permessiCambiati = valoreLivello !== a.accessLevel || !stesseAziende;
  const cambiata = credenzialiCambiate || permessiCambiati;
  const amministratore = valoreLivello === 'administrator';
  const senzaAzienda = !amministratore && valoreLivello !== 'none' && valoreAziende.length === 0;
  const senzaLivello = valoreLivello === 'none' && (a.hasCredentials || credenzialiCambiate);
  const errore = salva.error instanceof ErroreApi ? salva.error : null;

  const applica = () => {
    const dati: { email?: string; password?: string; accessLevel?: string; companyIds?: number[] } = {};
    if (valoreEmail !== (a.email ?? '')) dati.email = valoreEmail;
    if (password.length > 0) dati.password = password;
    if (valoreLivello !== a.accessLevel) dati.accessLevel = valoreLivello;
    if (!stesseAziende) dati.companyIds = valoreAziende;
    salva.mutate(dati, {
      onSuccess: () => {
        setEmail(null);
        setPassword('');
        setConferma('');
        setLivello(null);
        setAziende(null);
      },
    });
  };

  const scegliAzienda = (id: number, presa: boolean) =>
    setAziende(presa ? [...valoreAziende, id] : valoreAziende.filter((x) => x !== id));

  return (
    <div className="card span-2">
      <div className="card-head">
        <span className="card-title">{t('Accesso al programma')}</span>
        {a.suspendedAt ? (
          <span className="badge badge-warn" style={{ marginLeft: 'auto' }}>
            {t('sospesa')}
          </span>
        ) : (
          a.hasCredentials && (
            <span className="dim" style={{ marginLeft: 'auto', fontSize: 12 }}>
              {a.lastLoginAt
                ? `${t('ultimo accesso')} ${data(a.lastLoginAt)}`
                : t('non è mai entrato')}
            </span>
          )
        )}
      </div>
      <div className="card-body">
        {errore && (
          <div className="avviso" role="alert" style={{ marginBottom: 12 }}>
            <span className="avviso-segno">▲</span>
            <span>{errore.message}</span>
          </div>
        )}

        {!a.hasCredentials && (
          <p className="dim" style={{ marginTop: 0, fontSize: 13 }}>
            {t('Questo operatore non ha ancora l’accesso. Scrivi indirizzo e password e li avrà.')}
          </p>
        )}

        <div className="fields">
          <div className="field">
            <label htmlFor="acc-livello">{t('Livello di accesso')}</label>
            <select
              className="input"
              id="acc-livello"
              value={valoreLivello}
              onChange={(e) => setLivello(e.target.value)}
            >
              {valoreLivello === 'none' && (
                <option value="none" disabled>
                  {t('Scegli…')}
                </option>
              )}
              {LIVELLI.map((l) => (
                <option key={l} value={l}>
                  {etichettaLivello(l)}
                </option>
              ))}
            </select>
            {senzaLivello && (
              <span className="hint" style={{ color: 'var(--tomato)' }}>
                {t('Scegli il livello: senza, non entra.')}
              </span>
            )}
          </div>
          <div className="field">
            <label>{t('Aziende in cui lavora')}</label>
            {amministratore ? (
              <span className="dim" style={{ fontSize: 13, paddingTop: 6 }}>
                {t('Gli amministratori entrano in tutte le aziende.')}
              </span>
            ) : (
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', paddingTop: 4 }}>
                {a.companies.map((c) => (
                  <label className="check" key={c.id} htmlFor={`acc-az-${c.id}`}>
                    <input
                      id={`acc-az-${c.id}`}
                      type="checkbox"
                      checked={valoreAziende.includes(c.id)}
                      onChange={(e) => scegliAzienda(c.id, e.target.checked)}
                    />
                    <span>{c.name}</span>
                  </label>
                ))}
              </div>
            )}
            {senzaAzienda && (
              <span className="hint" style={{ color: 'var(--tomato)' }}>
                {t('Scegline almeno una: senza, entra e non trova niente.')}
              </span>
            )}
          </div>
          <div className="field span-2">
            <label htmlFor="acc-email">{t('Indirizzo e-mail con cui entra')}</label>
            <input
              className="input"
              id="acc-email"
              type="email"
              autoComplete="off"
              value={valoreEmail}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="field">
            <label htmlFor="acc-pw">
              {a.hasCredentials ? t('Password nuova') : t('Password')}
              {/* l'impronta non torna indietro: qui non c'è niente da
                  leggere, si può solo scriverne una nuova */}
              {a.hasCredentials && (
                <span className="dim" style={{ fontWeight: 400 }}>
                  {t(' — lascia vuoto per non cambiarla')}
                </span>
              )}
            </label>
            <input
              className="input"
              id="acc-pw"
              type="password"
              autoComplete="new-password"
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {/* il pulsante si spegneva e niente diceva perché */}
            {password.length > 0 && password.length < 8 && (
              <span className="hint">{t('Almeno 8 caratteri.')}</span>
            )}
          </div>
          <div className="field">
            <label htmlFor="acc-pw2">{t('Ripetila')}</label>
            <input
              className="input"
              id="acc-pw2"
              type="password"
              autoComplete="new-password"
              value={conferma}
              onChange={(e) => setConferma(e.target.value)}
            />
            {diverse && (
              <span className="hint" style={{ color: 'var(--tomato)' }}>
                {t('Le due password non coincidono.')}
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'center' }}>
          <button
            className="btn btn-primary"
            disabled={
              !cambiata ||
              diverse ||
              // la ripetizione lasciata vuota non è una conferma
              (password.length > 0 && conferma !== password) ||
              senzaAzienda ||
              senzaLivello ||
              salva.isPending ||
              (password.length > 0 && password.length < 8)
            }
            onClick={applica}
          >
            {salva.isPending ? t('Salvo…') : t('Salva l’accesso')}
          </button>

          {a.hasCredentials && (
            <button
              className="btn"
              style={{ marginLeft: 'auto' }}
              disabled={salva.isPending}
              onClick={() => salva.mutate({ suspended: !a.suspendedAt })}
              title={
                a.suspendedAt
                  ? t('Torna a poter entrare')
                  : t('Non entra più, ma resta tutto quello che ha fatto')
              }
            >
              {a.suspendedAt ? t('Riattiva l’utenza') : t('Sospendi l’utenza')}
            </button>
          )}
        </div>

        {password.length > 0 && (
          <p className="dim" style={{ fontSize: 13, marginBottom: 0, marginTop: 10 }}>
            {t('Cambiando la password a {nome} si chiudono anche le sue sessioni aperte, e gli arriva un messaggio.', { nome })}
          </p>
        )}
      </div>
    </div>
  );
}
